/**
 * React Query client configuration for CRM operations.
 */

import type { DefaultOptions } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';

import { isTransientError } from '@/lib/errors';

/**
 * Shared React Query defaults for every QueryClient in the app.
 * Retries only transient errors (NetworkError/TimeoutError/5xx) a few times and
 * refetches on reconnect, so queries self-heal after connectivity blips. Both
 * createCRMQueryClient and the StaffCsvImportApp embed use this single object.
 */
export const defaultQueryOptions: DefaultOptions = {
  queries: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount: number, error: unknown) =>
      isTransientError(error) && failureCount < 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  mutations: {
    retry: 0,
  },
};

/**
 * Create a configured QueryClient for CRM operations.
 */
export function createCRMQueryClient(): QueryClient {
  return new QueryClient({ defaultOptions: defaultQueryOptions });
}
